import { PortfolioResponse } from '@bitte-ai/types';
import { Copy } from 'lucide-react';
import { useState } from 'react';
import { formatTokenAmount } from '../../../lib/utils';
import { AssetItem } from './AssetItem';
import { DashboardHeader } from './DashboardHeader';

const calculatePortfolioChange = (data: PortfolioResponse): number => {
  let totalWeightedChange = 0;
  const totalValue = data.portfolioValue;

  data.significantAssets.forEach((asset) => {
    if (asset.priceChanges?.day !== undefined) {
      // Calculate weight of this asset in the portfolio
      const weight = asset.usdValue / totalValue;
      // Add weighted contribution to total change
      totalWeightedChange += asset.priceChanges.day * weight;
    }
  });

  return totalWeightedChange;
};

const useAccountOverview = (data: PortfolioResponse) => {
  const [showAllAssets, setShowAllAssets] = useState<boolean>(false);

  const portfolioChange = calculatePortfolioChange(data);
  const isPriceChangePositive = portfolioChange > 0;

  const visibleAssets = showAllAssets
    ? data.significantAssets
    : data.significantAssets.slice(0, 3);

  const remainingAssetsCount = data.significantAssets.length - 3;

  return {
    showAllAssets,
    setShowAllAssets,
    portfolioChange,
    isPriceChangePositive,
    visibleAssets,
    remainingAssetsCount,
  };
};

interface AccountOverviewProps {
  data: PortfolioResponse;
  walletAddress: string;
}

export function AccountOverview({ data, walletAddress }: AccountOverviewProps) {
  const {
    showAllAssets,
    setShowAllAssets,
    portfolioChange,
    isPriceChangePositive,
    visibleAssets,
    remainingAssetsCount,
  } = useAccountOverview(data);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(walletAddress);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <div className="bitte-flex bitte-flex-col bitte-items-center bitte-justify-center bitte-w-full bitte-bg-[#18181b] bitte-rounded-lg bitte-p-6 bitte-space-y-6 bitte-text-[#FAFAFA]">
      <DashboardHeader />

      <div className="bitte-grid bitte-grid-cols-1 lg:bitte-grid-cols-6 bitte-gap-4 bitte-w-full">
        <div className="lg:bitte-col-span-2 bitte-flex bitte-flex-col">
          <div className="bitte-text-[10px] bitte-text-[#969696] bitte-font-semibold bitte-uppercase bitte-mb-3">
            OVERVIEW
          </div>

          <div className="bitte-bg-[#292929] bitte-rounded-md bitte-flex-1 bitte-overflow-hidden bitte-flex bitte-flex-col bitte-justify-between">
            {walletAddress ? (
              <div className="bitte-flex bitte-items-center bitte-gap-2 bitte-px-4 bitte-py-6 bitte-border-b bitte-border-b-[#4F4848]">
                <span className="bitte-text-sm bitte-truncate">
                  {walletAddress}
                </span>
                <button
                  type="button"
                  className="bitte-text-[#60A5FA]"
                  onClick={handleCopy}
                  title="Copy address"
                >
                  <Copy className="bitte-w-4 bitte-h-4" />
                </button>
              </div>
            ) : null}

            <div className="bitte-p-4 bitte-pt-6">
              <div className="bitte-text-[8.5px] bitte-text-[#969696] bitte-font-semibold bitte-uppercase bitte-mb-1">
                BALANCE
              </div>
              <div className="bitte-text-2xl bitte-font-bold bitte-mb-3">
                $
                {data.portfolioValue.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}
              </div>
              <div
                className={`bitte-text-sm bitte-font-semibold bitte-mb-6 ${isPriceChangePositive ? 'bitte-text-[#4ADE80]' : 'bitte-text-[#F87171]'}`}
              >
                {portfolioChange > 0 ? '+' : ''}
                {portfolioChange.toFixed(2)}%
              </div>
            </div>

            <div className="bitte-p-4 bitte-pt-6">
              <div className="bitte-grid bitte-grid-cols-2 bitte-gap-4">
                <div className="bitte-bg-[#363638] bitte-rounded bitte-px-4 bitte-py-2">
                  <div className="bitte-text-[8.5px] bitte-text-[#969696] bitte-font-semibold bitte-uppercase bitte-mb-1">
                    TOKENS
                  </div>
                  <div className="bitte-text-sm">
                    {data.significantAssets.length}
                  </div>
                </div>
                <div className="bitte-bg-[#363638] bitte-rounded bitte-px-4 bitte-py-2">
                  <div className="bitte-text-[8.5px] bitte-text-[#969696] bitte-font-semibold bitte-uppercase bitte-mb-1">
                    NETWORKS
                  </div>
                  <div className="bitte-text-sm">
                    {data.chainsWithGas.length}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bitte-flex bitte-flex-col lg:bitte-col-span-4">
          <div className="bitte-text-xs bitte-text-gray-500 bitte-uppercase bitte-mb-3 bitte-invisible">
            SPACER
          </div>

          <div className="bitte-bg-[#292929] bitte-rounded-md bitte-flex-1 bitte-p-4 bitte-flex bitte-flex-col">
            <div className="bitte-grid bitte-grid-cols-3 bitte-text-xs bitte-text-gray-500 bitte-uppercase bitte-mb-3">
              <div className="bitte-text-[8.5px] bitte-text-[#969696] bitte-font-semibold">
                ASSET
              </div>
              <div className="bitte-text-[8.5px] bitte-text-[#969696] bitte-font-semibold bitte-text-right">
                BALANCE
              </div>
              <div className="bitte-text-[8.5px] bitte-text-[#969696] bitte-font-semibold bitte-text-right">
                VALUE
              </div>
            </div>

            <div className="bitte-overflow-y-auto bitte-max-h-[400px] bitte-flex-1">
              {visibleAssets.map((asset, index) => (
                <AssetItem
                  key={asset.symbol}
                  asset={{
                    id: index + 1,
                    symbol: asset.symbol,
                    amount: formatTokenAmount(asset.balances.balance),
                    value: `$${asset.usdValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
                    change: `${asset.priceChanges?.day ? (asset.priceChanges.day > 0 ? '+' : '') + asset.priceChanges.day.toFixed(2) : '0'}%`,
                    isPositive: asset.priceChanges?.day
                      ? asset.priceChanges.day > 0
                      : false,
                    network: asset.chains[0] || 'Unknown',
                    chainIcon: asset.icon || '',
                    tokenIcon: asset.meta.tokenIcon || '',
                  }}
                />
              ))}
            </div>

            {remainingAssetsCount > 0 && (
              <div className="bitte-text-center bitte-mt-4">
                <button
                  type="button"
                  className="bitte-text-[8.5px] bitte-text-[#60A5FA] bitte-font-bold bitte-uppercase"
                  onClick={() => setShowAllAssets(!showAllAssets)}
                >
                  {showAllAssets ? 'SHOW LESS' : `${remainingAssetsCount} MORE`}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
